import React, { useEffect, useMemo, useState } from 'react';
import DogIcon from '../../components/Custom Icons/dog_icon';
import CatIcon from '../../components/Custom Icons/cat_icon';
import HorseIcon from '../../components/Custom Icons/horse_icon';
import GoatIcon from '../../components/Custom Icons/goat_icon';
import PigIcon from '../../components/Custom Icons/pig_icon';
import ReptileIcon from '../../components/Custom Icons/reptile_icon';
import ChickenIcon from '../../components/Custom Icons/chicken_icon';
import { useBean } from '../../context/BeanContext';

type AnimalKind = 'dog' | 'cat' | 'horse' | 'goat' | 'pig' | 'reptile' | 'bird';
type VitalKind = 'heart' | 'resp' | 'weight' | 'temp' | 'mucous';

type VitalGuide = {
  title: string;
  howTo: string;
  normal: string;
  tip: string;
};

type AnimalGuide = {
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  vitals: Record<VitalKind, VitalGuide>;
};

const GUIDE: Record<AnimalKind, AnimalGuide> = {
  dog: {
    name: 'Dog',
    icon: DogIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Feel inside the rear leg (femoral pulse) or place a hand on the chest behind the left elbow. Count beats for 15 seconds; the app converts this to bpm for you.',
        normal: 'Resting: 60–140 bpm (smaller dogs trend higher).',
        tip: 'Measure when calm and resting, not after exercise.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Watch chest rise and fall at rest. One rise + one fall = 1 breath. Count for 15 seconds and the app converts this to breaths/min for you.',
        normal: 'Resting: 10–30 breaths/min.',
        tip: 'Record during sleep for the most reliable baseline.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use a digital pet scale or weigh yourself holding your dog and subtract your own weight.',
        normal: 'Track trend over time rather than one number.',
        tip: 'Weigh on the same scale, same time of day.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Use a lubricated rectal digital thermometer and hold gently until reading completes.',
        normal: '38.0–39.2 °C (100.4–102.5 °F).',
        tip: 'If consistently above or below range, contact your vet.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Lift lip and check gum color and moisture.',
        normal: 'Healthy gums are pink and moist.',
        tip: 'Pale/white/blue/very red or tacky gums need prompt vet review.',
      },
    },
  },
  cat: {
    name: 'Cat',
    icon: CatIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Place a hand over the left chest behind the elbow or feel pulse on inner thigh. Count for 15 seconds; the app converts this to bpm for you.',
        normal: 'Resting: 140–220 bpm.',
        tip: 'Allow your cat to settle quietly before measuring.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Count chest movements while resting or asleep for 15 seconds; the app converts this to breaths/min for you.',
        normal: 'Resting: 20–30 breaths/min.',
        tip: 'Persistent rates above ~40 at rest should be reviewed.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use a baby/pet scale or weigh yourself with and without your cat.',
        normal: 'Small weekly changes can be clinically important.',
        tip: 'Log weight weekly if managing kidney disease.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Measure with a lubricated rectal digital thermometer.',
        normal: '38.1–39.2 °C (100.5–102.5 °F).',
        tip: 'Stress can transiently increase temperature.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Check gums for color and moisture under good light.',
        normal: 'Pink and moist.',
        tip: 'Pale, blue, brick-red, or dry gums are abnormal.',
      },
    },
  },
  horse: {
    name: 'Horse',
    icon: HorseIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Use stethoscope behind left elbow or feel facial artery under jaw. Count for 30 seconds × 2.',
        normal: 'Resting: 28–44 bpm.',
        tip: 'Elevated resting HR can indicate pain or stress.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Observe flank movement at rest for 15 seconds; the app converts this to breaths/min for you.',
        normal: 'Resting: 8–16 breaths/min.',
        tip: 'Count before handling or feeding for baseline accuracy.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use a livestock scale or weight tape at the same body position each time.',
        normal: 'Trend over weeks is most useful.',
        tip: 'Use the same tape method and measurement site each check.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Rectal digital thermometer with lubricant.',
        normal: '37.2–38.3 °C (99.0–101.0 °F).',
        tip: 'Any fever with lethargy or colic signs needs urgent assessment.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Check gum color and capillary refill (press gum, release, observe refill).',
        normal: 'Pink, moist; refill ~1–2 seconds.',
        tip: 'Dark red, muddy, pale, or delayed refill are concerning.',
      },
    },
  },
  goat: {
    name: 'Goat',
    icon: GoatIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Use stethoscope over left chest, count 30 seconds × 2.',
        normal: 'Resting: 70–110 bpm.',
        tip: 'Kids usually run higher rates than adults.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Observe chest/flank movement at rest for 15 seconds; the app converts this to breaths/min for you.',
        normal: 'Resting: 15–30 breaths/min.',
        tip: 'Panting at rest can indicate heat stress or illness.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use livestock scale or weight tape specific for goats.',
        normal: 'Monitor trend and body condition score together.',
        tip: 'Rapid loss needs prompt dietary and medical review.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Rectal digital thermometer.',
        normal: '38.6–40.0 °C (101.5–104.0 °F).',
        tip: 'Interpret with behavior, appetite, and hydration signs.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Check lower eyelid/gums for color.',
        normal: 'Pink and moist.',
        tip: 'Very pale membranes can suggest anemia/parasitism.',
      },
    },
  },
  pig: {
    name: 'Pig',
    icon: PigIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Use stethoscope behind elbow; count 30 seconds × 2.',
        normal: 'Resting adult: ~60–100 bpm.',
        tip: 'Stress can raise HR quickly in pigs.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Observe thoracic movement quietly for 15 seconds; the app converts this to breaths/min for you.',
        normal: 'Resting: ~10–20 breaths/min (adult).',
        tip: 'Open-mouth breathing is an urgent warning sign.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use livestock scale or girth/length formula consistently.',
        normal: 'Track trend and appetite together.',
        tip: 'Use same method each time for comparable results.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Rectal digital thermometer.',
        normal: '38.7–39.8 °C (101.7–103.6 °F).',
        tip: 'Heat stress can elevate temperature quickly.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Check oral mucosa for color and moisture.',
        normal: 'Pink and moist.',
        tip: 'Pale, blue, or dark red membranes are abnormal.',
      },
    },
  },
  reptile: {
    name: 'Reptile',
    icon: ReptileIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Use reptile-safe Doppler/stethoscope where possible; measure with species-appropriate handling.',
        normal: 'Highly species and temperature dependent.',
        tip: 'Always record enclosure temperature with the reading.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Observe subtle chest/throat movement while undisturbed.',
        normal: 'Varies widely by species and ambient temperature.',
        tip: 'Wheezing, bubbles, or open-mouth breathing are concerning.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use a gram scale for small reptiles or platform scale for large species.',
        normal: 'Weekly trend is more informative than a single check.',
        tip: 'Monitor with appetite, shed quality, and activity.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Cloacal temperature with digital probe when indicated.',
        normal: 'Reference range depends on species and husbandry zone.',
        tip: 'Confirm habitat thermal gradient first.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Inspect oral cavity tissue color and moisture if safe to do so.',
        normal: 'Should not be tacky, ulcerated, or discolored.',
        tip: 'Any oral plaques, petechiae, or severe dryness warrant vet review.',
      },
    },
  },
  bird: {
    name: 'Bird',
    icon: ChickenIcon,
    vitals: {
      heart: {
        title: 'Heart Rate',
        howTo: 'Auscultate gently with minimal restraint; count for 15 seconds and the app converts this to bpm for you.',
        normal: 'Species dependent; generally much higher than mammals.',
        tip: 'Avoid prolonged restraint to reduce stress artifacts.',
      },
      resp: {
        title: 'Respiratory Rate',
        howTo: 'Observe tail bob and chest movement at rest.',
        normal: 'Species dependent; small birds breathe faster.',
        tip: 'Open-mouth breathing or marked tail bobbing is urgent.',
      },
      weight: {
        title: 'Weight',
        howTo: 'Use a gram scale and weigh at the same time each day.',
        normal: 'Daily trend can detect illness early in birds.',
        tip: 'Even small percentage losses are clinically significant.',
      },
      temp: {
        title: 'Temperature',
        howTo: 'Measure only when needed with avian-safe technique.',
        normal: 'Usually higher than mammals (~40–42 °C in many species).',
        tip: 'Interpret with species, age, and stress level.',
      },
      mucous: {
        title: 'Mucous Membranes',
        howTo: 'Assess oral mucosa color/moisture if handling is safe.',
        normal: 'Should appear moist and healthy, without pallor/cyanosis.',
        tip: 'Any discoloration plus breathing change is urgent.',
      },
    },
  },
};

const VITAL_ORDER: VitalKind[] = ['heart', 'resp', 'weight', 'temp', 'mucous'];

export function HowToVitals() {
  const [animal, setAnimal] = useState<AnimalKind>('dog');
  const { awardXP } = useBean();

  useEffect(() => {
    awardXP('how_to');
  }, [awardXP]);

  const current = useMemo(() => GUIDE[animal], [animal]);

  return (
    <article className="w-full max-w-5xl mx-auto pb-6">
      <div className="mb-5 p-4 rounded-2xl border border-primary-brown/20 bg-primary-brown/5">
        <h3 className="text-sm font-bold text-primary-brown uppercase tracking-widest mb-3">Select animal</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
          {(Object.keys(GUIDE) as AnimalKind[]).map((key) => {
            const item = GUIDE[key];
            const Icon = item.icon;
            const active = key === animal;
            return (
              <button
                key={key}
                onClick={() => setAnimal(key)}
                className={`rounded-xl border px-2 py-2 text-center transition-colors ${
                  active
                    ? 'bg-primary-brown text-white border-primary-brown'
                    : 'bg-white text-primary-brown border-primary-brown/25 hover:bg-primary-brown/5'
                }`}
              >
                <Icon
                  className={`mx-auto w-9 h-9 [&_.cls-1]:fill-current [&_.cls-2]:fill-current [&_.cls-3]:fill-current [&_path]:fill-current [&_ellipse]:fill-current [&_circle]:fill-current ${
                    active ? 'text-white brightness-0 invert opacity-100' : 'text-primary-brown opacity-100'
                  }`}
                />
                <span className="mt-1 block text-xs font-semibold">{item.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4">
        <h2 className="text-xl font-black text-primary-brown">How to check vitals for a {current.name.toLowerCase()}</h2>
        <p className="text-sm text-text-secondary mt-1">Follow a calm, consistent routine and log values in the Vitals page.</p>
      </div>

      <div className="space-y-3">
        {VITAL_ORDER.map((vital) => {
          const info = current.vitals[vital];
          return (
            <section key={vital} className="rounded-2xl border border-slate-200 bg-white p-4">
              <h4 className="text-base font-bold text-text-primary">{info.title}</h4>
              <p className="mt-2 text-sm text-text-secondary"><span className="font-semibold text-text-primary">How:</span> {info.howTo}</p>
              <p className="mt-1 text-sm text-text-secondary"><span className="font-semibold text-text-primary">Typical:</span> {info.normal}</p>
              <p className="mt-1 text-sm text-text-secondary"><span className="font-semibold text-text-primary">Tip:</span> {info.tip}</p>
            </section>
          );
        })}
      </div>
    </article>
  );
}
